import { useCallback, useLayoutEffect, useReducer, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import { stringifyFormEditorCanvasForCompare } from "@/features/form-editor/lib/stringify-form-editor-canvas";
import {
	readFormBaseline,
	rememberFormBaseline,
	shouldSkipFormHydration,
} from "@/features/form-editor/state/entity-type-form-mode-store";
import { useFormEditorStore } from "@/features/form-editor/state/form-editor-store";

/**
 * Tracks whether canvas state (title, sections, fields) differs from the last hydrated or saved baseline.
 */
export function useFormEditorDirty(editorHydrationKey: string | null): {
	isDirty: boolean;
	markBaselineCurrent: () => void;
} {
	const baselineRef = useRef<string | null>(null);
	const baselineAtKeyRef = useRef<string | null>(null);
	const [, bumpBaseline] = useReducer((x: number) => x + 1, 0);

	const canvas = useFormEditorStore(
		useShallow((s) => ({
			formTitle: s.formTitle,
			formDescription: s.formDescription,
			rootIds: s.rootIds,
			nodes: s.nodes,
			formRules: s.formRules,
		})),
	);

	const hydrationKeyRef = useRef(editorHydrationKey);
	hydrationKeyRef.current = editorHydrationKey;

	const markBaselineCurrent = useCallback(() => {
		const next = stringifyFormEditorCanvasForCompare(
			useFormEditorStore.getState(),
		);
		baselineRef.current = next;
		if (hydrationKeyRef.current) {
			rememberFormBaseline(hydrationKeyRef.current, next);
		}
		bumpBaseline();
	}, []);

	useLayoutEffect(() => {
		if (editorHydrationKey == null) {
			baselineRef.current = null;
			baselineAtKeyRef.current = null;
			return;
		}
		const retained =
			shouldSkipFormHydration(editorHydrationKey)
				? readFormBaseline(editorHydrationKey)
				: null;
		const next =
			retained ??
			stringifyFormEditorCanvasForCompare(useFormEditorStore.getState());
		baselineRef.current = next;
		baselineAtKeyRef.current = editorHydrationKey;
		if (retained == null) rememberFormBaseline(editorHydrationKey, next);
		bumpBaseline();
	}, [editorHydrationKey]);

	let isDirty = false;
	if (
		editorHydrationKey != null &&
		baselineAtKeyRef.current === editorHydrationKey &&
		baselineRef.current !== null
	) {
		isDirty =
			stringifyFormEditorCanvasForCompare({
				formTitle: canvas.formTitle,
				formDescription: canvas.formDescription,
				rootIds: canvas.rootIds,
				nodes: canvas.nodes,
				formRules: canvas.formRules,
			}) !== baselineRef.current;
	}

	return { isDirty, markBaselineCurrent };
}
